import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { ActionCreators } from 'redux-undo';
import {
  selectMetadataMappingStatus,
  selectMetadataMappingError,
  setMetadataMappingLoading,
  setMetadataMappingError,
  selectMetadataMappingMessages,
  addChatMessage,
  MetadataMappingMessage,
  selectLastUserMessageHistoryIndex,
  selectLastUserMessageIndex,
  selectHasUserMessages,
  removeMessagesFromIndex,
} from '../../../../reducers/metadataMappingReducer';
import { useAiAgentApiFacade } from '../../../../apis/facades/useAiAgentApiFacade';
import { AiChatMessage } from '../../../common/ai-chat/aiChatTypes';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import { DateTime } from 'luxon';
import { telemetry } from '../../../../telemetry';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../../../services/events';
import { useFunnelMapApiFacade } from '../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import {
  setFunnelMap,
  selectHistoryLength,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import { normalizeFunnelMapPositions } from '../../funnel-map-flow/normalizeFunnelMapPositions';
import { useAutoSaveContext } from '../../funnel-map-flow/auto-save/AutoSaveContext';
import { setIsPageLoading } from '../../../../reducers/canvasPageStateReducer';
import useUserInfo from '../../../../hooks/useUserInfo';

/**
 * IMPORTANT: This hook should NOT contain any useEffect hooks.
 * It is used in multiple places to retrieve data and provide callback functions.
 * Any side effects (like initializing messages array or calling "complete") should be handled
 * in the consuming components, not in this hook.
 */
export const useMetadataMapping = (funnelMapId: string) => {
  const dispatch = useDispatch();
  const { post_processMining } = useAiAgentApiFacade();
  const { get_funnelMap } = useFunnelMapApiFacade();
  const { funnelMapVersion } = useAutoSaveContext();
  const sendBiEvent = useSendBiEvent();
  const { updateHubspotContact } = useUserInfo();

  const status = useSelector(selectMetadataMappingStatus(funnelMapId));
  const error = useSelector(selectMetadataMappingError(funnelMapId));
  const messages = useSelector(selectMetadataMappingMessages(funnelMapId));
  const historyLength = useSelector(selectHistoryLength);
  const isLoading = status === 'loading' || status === 'completedFetchingData';

  const complete = useCallback(async () => {
    if (!funnelMapId) return;

    try {
      const funnelMap = (await get_funnelMap(funnelMapId, true)) as FunnelMap;
      funnelMapVersion.current = funnelMap.version;
      dispatch(setFunnelMap(normalizeFunnelMapPositions(funnelMap)));
      dispatch(setIsPageLoading({ isPageLoading: false }));
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      dispatch(setMetadataMappingLoading({ funnelMapId, isLoading: false }));
    }
  }, [dispatch, funnelMapId, get_funnelMap, funnelMapVersion]);

  const sendMessage = useCallback(
    async (messageContent: string) => {
      if (!funnelMapId) {
        return;
      }
      sendBiEvent({ name: AI_AGENTS_EVENTS.generateProcessMiningFromCanvas });
      updateHubspotContact({ last_ai_chat_date: Date.now() });

      // Capture history index BEFORE any mutations
      const historyIndex = historyLength;

      // Base message for API (no historyIndex - it's UI-only)
      const baseMessage: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.USER,
        content: messageContent,
        createdAt: DateTime.now().toISO(),
      };

      // Extended message for Redux (with historyIndex for undo support)
      const messageForRedux: MetadataMappingMessage = {
        ...baseMessage,
        historyIndex,
      };

      dispatch(
        addChatMessage({
          funnelMapId,
          message: messageForRedux,
        }),
      );

      try {
        dispatch(setMetadataMappingLoading({ funnelMapId, isLoading: true }));
        await post_processMining(funnelMapId, { message: baseMessage });
      } catch (error) {
        telemetry.captureError(error);
        dispatch(
          setMetadataMappingError({
            funnelMapId,
            error: `Couldn't generate results. Try again with a new prompt.`,
          }),
        );
      }
    },
    [funnelMapId, sendBiEvent, updateHubspotContact, dispatch, post_processMining, historyLength],
  );

  const latestAssistantMessage = useMemo(() => {
    const assistantMessages = messages.filter(
      (m) => m.role === ChatCompletionRequestMessageRole.ASSISTANT,
    );
    const lastMessage = assistantMessages[assistantMessages.length - 1];
    if (!lastMessage) return null;
    return typeof lastMessage.content === 'string' ? lastMessage.content : null;
  }, [messages]);

  const canSendMessage = status === 'idle' || status === 'error';

  const clearLoadingState = useCallback(() => {
    dispatch(setMetadataMappingLoading({ funnelMapId, isLoading: false }));
  }, [dispatch, funnelMapId]);

  const hasUserMessages = useSelector(selectHasUserMessages(funnelMapId));
  const lastUserMessageHistoryIndex = useSelector(selectLastUserMessageHistoryIndex(funnelMapId));
  const lastUserMessageIndex = useSelector(selectLastUserMessageIndex(funnelMapId));

  const undoLastMessage = useCallback(() => {
    if (lastUserMessageHistoryIndex === null || lastUserMessageIndex === null) return;

    // Safety check: ensure history index is still valid
    if (lastUserMessageHistoryIndex >= historyLength) {
      console.warn('History index is no longer valid - cannot undo past manual undo operations');
      return;
    }

    dispatch(ActionCreators.jumpToPast(lastUserMessageHistoryIndex));

    dispatch(
      removeMessagesFromIndex({
        funnelMapId,
        fromIndex: lastUserMessageIndex,
      }),
    );
  }, [funnelMapId, lastUserMessageHistoryIndex, lastUserMessageIndex, dispatch, historyLength]);

  // Only allow undo if:
  // 1. Status is idle (not loading)
  // 2. There are user messages
  // 3. The history index is still valid (user hasn't manually undone past it)
  const canUndoLastMessage =
    status === 'idle' &&
    hasUserMessages &&
    lastUserMessageHistoryIndex !== null &&
    lastUserMessageHistoryIndex < historyLength;

  return {
    messages,
    isLoading,
    error,
    canSendMessage,
    sendMessage,
    status,
    latestAssistantMessage,
    complete,
    clearLoadingState,
    undoLastMessage,
    canUndoLastMessage,
  };
};
